import type { CancelGoodsDisposition, FulfillmentOrderLine } from '@/infrastructure/FulfillmentAdminClient';
import { canCancelLine } from '@/utils/fulfillmentLineStatus';

/** JIT choices require purchase or arrival evidence; AWAITING_INBOUND alone never proves a purchase. */
export type CancelGoodsKind = 'EXISTING' | 'BOUGHT' | 'NOT_BOUGHT';
export type CancelGoodsChoice = 'RETURN' | 'KEEP';

export interface CancelGoodsLine {
  lineNo: number;
  sku: string;
  name: string;
  quantity: number;
  kind: CancelGoodsKind;
  /** Whether an arrival is on record, which determines Return's disposition. */
  arrived: boolean;
}

const EXISTING_INVENTORY = 'EXISTING_INVENTORY';
// INBOUNDED/READY prove legacy arrival even without V2 reservations, matching Facade's OrderInboundState.
const hasArrived = (line: FulfillmentOrderLine): boolean =>
  line.inboundReversible === true ||
  (line.locations ?? []).some((location) => location.qty > 0) ||
  line.lineStatus === 'INBOUNDED' ||
  line.lineStatus === 'READY';

export type { CancelGoodsDisposition } from '@/infrastructure/FulfillmentAdminClient';

const kindOf = (line: FulfillmentOrderLine): CancelGoodsKind => {
  if (line.sourcingRoute === EXISTING_INVENTORY) {
    return 'EXISTING';
  }
  return hasArrived(line) || Boolean(line.purchaseOrderNumber?.trim()) || Boolean(line.lineTrackingNumber?.trim())
    ? 'BOUGHT'
    : 'NOT_BOUGHT';
};

/** The lines the cancel still cancels, each with what can be decided for its goods. */
export const cancelGoodsLinesOf = (lines: readonly FulfillmentOrderLine[]): CancelGoodsLine[] =>
  lines
    .filter((line) => canCancelLine(line.lineStatus))
    .map((line) => ({
      lineNo: line.lineNo,
      sku: line.sku,
      name: line.productName ?? '',
      quantity: line.totalQuantity,
      kind: kindOf(line),
      arrived: hasArrived(line),
    }));

/** True when every bought line has return or keep. */
export const cancelGoodsAnswered = (
  lines: readonly CancelGoodsLine[],
  choices: Readonly<Partial<Record<number, CancelGoodsChoice>>>,
): boolean => lines.every((line) => line.kind !== 'BOUGHT' || choices[line.lineNo] !== undefined);

/** Require complete choices rather than silently turning an unanswered return/keep into keep. */
export const cancelGoodsDispositions = (
  lines: readonly CancelGoodsLine[],
  choices: Readonly<Partial<Record<number, CancelGoodsChoice>>>,
): CancelGoodsDisposition[] => {
  if (!cancelGoodsAnswered(lines, choices)) {
    throw new Error('Choose Return or Keep for every purchased line');
  }
  return lines.map((line) => ({
    lineNo: line.lineNo,
    receivedGoods:
      line.kind === 'NOT_BOUGHT'
        ? 'NOT_PURCHASED'
        : line.kind === 'EXISTING' || choices[line.lineNo] === 'KEEP'
          ? 'KEEP_IN_STOCK'
          : line.arrived
            ? 'WRITE_OFF'
            : 'RETURN_TO_SELLER',
  }));
};

const ACTION_WORDS: Record<CancelGoodsChoice, string> = { RETURN: 'return to seller', KEEP: 'keep in stock' };

/**
 * The goods note the server keeps on the cancel and posts to the comments, one clause per line that needs action:
 * `line 1 100004607 x4: back to stock (existing inventory); line 2 100004659 x6: return to seller`. Empty when no line
 * needs any; nothing-bought lines are left out.
 */
export const describeCancelGoods = (
  lines: readonly CancelGoodsLine[],
  choices: Readonly<Partial<Record<number, CancelGoodsChoice>>>,
): string =>
  lines
    .map((line) => {
      const head = `line ${line.lineNo} ${line.sku} x${line.quantity}`;
      if (line.kind === 'EXISTING') {
        return `${head}: back to stock (existing inventory)`;
      }
      const choice = line.kind === 'BOUGHT' ? choices[line.lineNo] : undefined;
      return choice ? `${head}: ${ACTION_WORDS[choice]}` : '';
    })
    .filter((clause) => clause !== '')
    .join('; ');
